import {
    createContext,
    type MouseEvent,
    type ReactNode,
    type SyntheticEvent,
    useContext,
    useId,
    useLayoutEffect,
} from "react";
import { type DraggableSyntheticListeners, useDraggable } from "@dnd-kit/core";
import { useSelector } from "@tanstack/react-store";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useDataGridContext } from "../../DataGridContext";
import type { DataGridAtoms, DataGridRangeAction } from "../../types";
import styles from "../../grid/Grid.module.scss";

const NO_DRAG_DATA = {};

interface RangeActionDrag {
    action: DataGridRangeAction;
    setNodeRef: (element: HTMLElement | null) => void;
    listeners: DraggableSyntheticListeners;
    isDragging: boolean;
}

const RangeActionContext = createContext<RangeActionDrag | null>(null);

const selectAction = (action: DataGridRangeAction | null) => action;

/**
 * Owns the range action's draggable. Render it outside the grid's own
 * `GridDnd`, so the drag joins the app's `DndContext`, not the table's.
 */
export const RangeActionSource = ({
    atoms,
    children,
}: {
    atoms: DataGridAtoms;
    children: ReactNode;
}) => {
    const id = useId();
    const action = useSelector(atoms.rangeAction, selectAction);
    const { setNodeRef, listeners, isDragging } = useDraggable({
        id: `dg-range-action:${id}`,
        data: action?.dragData ?? NO_DRAG_DATA,
        disabled: action === null,
    });
    const value = action ? { action, setNodeRef, listeners, isDragging } : null;
    return (
        <RangeActionContext.Provider value={value}>
            {children}
        </RangeActionContext.Provider>
    );
};

const stopPropagation = (event: SyntheticEvent) => event.stopPropagation();

// Focus stays on the active cell, and the grid doesn't start a range.
const keepGridFocus = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
};

/**
 * The button on the range's top-end corner. Mousedowns and double clicks
 * stay on it, so the grid neither starts a range nor opens the row under it.
 */
export const RangeActionButton = ({ atoms }: { atoms: DataGridAtoms }) => {
    const drag = useContext(RangeActionContext);
    const isEditorOpen = useSelector(
        atoms.activeEditor,
        (editor) => editor !== null,
    );
    if (!drag || isEditorOpen) {
        return null;
    }
    const { action, setNodeRef, listeners, isDragging } = drag;
    const handleClick = (event: MouseEvent) => {
        event.stopPropagation();
        action.onClick();
    };
    return (
        <Tooltip content={action.label} disabled={isDragging}>
            <button
                type="button"
                ref={setNodeRef}
                {...listeners}
                tabIndex={-1}
                aria-label={action.label}
                className={styles.rangeAction}
                data-dt-range-action=""
                onMouseDown={keepGridFocus}
                onClick={handleClick}
                onDoubleClick={stopPropagation}
            >
                {action.icon}
            </button>
        </Tooltip>
    );
};

/**
 * Puts `action` on the range's top-right corner in a `rangeSelection` grid;
 * `undefined` shows none. When the action follows the range, render this
 * from a small component that reads it, so a range change re-renders only
 * that component and not the table.
 */
export const DataGridRangeActionSlot = ({
    action,
}: {
    action: DataGridRangeAction | undefined;
}) => {
    const rangeAction = useDataGridContext().visit(
        (instance) => instance.atoms.rangeAction,
    );
    useLayoutEffect(() => {
        rangeAction.set(action ?? null);
    }, [rangeAction, action]);
    useLayoutEffect(() => () => rangeAction.set(null), [rangeAction]);
    return null;
};
